import { Card, IconButton, Link, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import * as React from "react";
import { useContext, useEffect, useRef, useState } from "react";
import { useGetOne, useShowController } from "react-admin";
import { useNavigate } from "react-router-dom";
import { OpenTabsContext } from "../../OpenTabsContext";
import { hmLightTheme } from "../../themes/hmTheme";
import CustomTabSmallButton from "../../unit/CustomTabSmallButton";
import FieldShow from "../../unit/FieldShow";
import formatCurrency from "../../unit/formatCurrency";
import formatInt from "../../unit/formatInt";
import HmSubHeaderTitle from "../../unit/HmSubHeaderTitle";
import useNotify from "../../unit/useNotify";
import { ReferenceNameField } from "../ReferenceNameField";
import CustomAsideCard from "../../unit/CustomAsideCard";
import PaidOffTenDaysList from "../../loans/PaidOffTenDaysList";
import CollateralDetailDialog from "../CollateralDetail";
import { Print } from "@mui/icons-material";
import ApplicationPrint from "../ApplicationPrint";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import AppStageStatus from "./AppStageStatus";

const BaseCustomTab = ({ ...props }) => {
  const [activeTab, setActiveTab] = React.useState(0);
  const appId = props.appId;
  const record = props.record;

  const handleChange = (newValue: number) => {
    setActiveTab(newValue);
  };
  return (
    <Box sx={{ width: "100%", pt: 1 }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        {/* if activeTab == 0, set className="Mui-selected"  */}
        <CustomTabSmallButton
          variant="text"
          onClick={() => handleChange(0)}
          className={activeTab === 0 ? "Mui-selected" : ""}
        >
          Primary
        </CustomTabSmallButton>
        {record?.coapplicant_id && (
          <CustomTabSmallButton
            variant="text"
            onClick={() => handleChange(1)}
            className={activeTab === 1 ? "Mui-selected" : ""}
          >
            Co-Applicant
          </CustomTabSmallButton>
        )}
        {record?.guarantor_id && (
          <CustomTabSmallButton
            variant="text"
            onClick={() => handleChange(2)}
            className={activeTab === 2 ? "Mui-selected" : ""}
          >
            Guarantor
          </CustomTabSmallButton>
        )}
      </Box>
      <Box sx={{ display: activeTab === 0 ? "block" : "none", pt: 2, pb: 2 }}>
        <LoadApplicant appId={appId} record={record} />
      </Box>
      {record?.coapplicant_id && (
        <Box sx={{ display: activeTab === 1 ? "block" : "none", pt: 2, pb: 2 }}>
          <LoadCoApplicant appId={appId} record={record} />
        </Box>
      )}
      {record?.guarantor_id && (
        <Box sx={{ display: activeTab === 2 ? "block" : "none", pt: 2, pb: 2 }}>
          <LoadGuarantor appId={appId} record={record} />
        </Box>
      )}
    </Box>
  );
};
const UserNumberLinkField = ({ ...props }) => {
  const id = props.id;
  const { openGlobalTabs } = useContext(OpenTabsContext);
  const { successNotify, errorNotify, infoNotify } = useNotify();
  const navigate = useNavigate();
  const handleClick = (id: any) => {
    if (openGlobalTabs.length >= 6) {
      if (openGlobalTabs.includes("customer-" + id + "-")) {
        navigate(`/userSummary/` + id);
      } else {
        errorNotify("Cannot open more than 6 tabs.");
        return false;
      }
    } else {
      navigate(`/userSummary/` + id);
    }
  };

  return (
    <Typography variant="body2" color="black" sx={{ mb: 1 }}>
      <b>User #:</b>
      {id ? (
        <Link
          component="span"
          onClick={() => handleClick(id)}
          sx={{
            cursor: "pointer",
            textDecoration: "none",
            fontFamily: hmLightTheme?.palette?.numberFontFamily,
            "&.RaLink-link": {
              color: "#1976d2",
            },
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {id}
        </Link>
      ) : (
        "N/A"
      )}
    </Typography>
  );
};
const LoadApplicant = ({ ...props }) => {
  const paramAppId = props.appId;
  let appId = "";
  let record = props.record;
  if (paramAppId != undefined && record != undefined) {
    appId = paramAppId;
  } else {
    const controllerProps = useShowController();
    record = controllerProps.record;
    appId = record ? record.id : "";
  }
  const [recordData, setRecordData] = useState<any>(null);
  const { successNotify, errorNotify, infoNotify } = useNotify();
  const { data, isPending, error } = useGetOne("applications/getApplicant", {
    id: record.iUserId,
  });
  useEffect(() => {
    setRecordData(data);
  }, [record.iUserId, data]);

  if (error) {
    errorNotify("Error loading Application " + error);
  }
  if (isPending) {
    return <Typography>Application Loading</Typography>;
  }
  if (!recordData) {
    return "No Data";
  }

  const ReferenceField = (recordData: any) => {
    const employment_id = recordData ? recordData.employment_id : 0;
    const counselor_id = recordData ? recordData.counselor_id : 0;
    const iCounselorVerification = recordData
      ? recordData.iCounselorVerification
      : false;
    if (employment_id > 0 || counselor_id > 0) {
      if (counselor_id > 0 || iCounselorVerification) {
        return (
          <Typography variant="body2" color="textSecondary">
            <b>{`Reference : `} </b>
            <ReferenceNameField />
          </Typography>
        );
      }
    } else {
      return <FieldShow title="Reference" value="N/A" />;
    }
  };
  return (
    <>
      <UserNumberLinkField id={recordData ? recordData?.iUserId : ""} />
      <FieldShow
        title="Name"
        value={
          (recordData && recordData?.sFname ? recordData?.sFname : "") +
          ` ` +
          (recordData && recordData?.sLname ? recordData?.sLname : "")
        }
      />
      <FieldShow
        title="Mobile"
        value={recordData ? recordData?.sMobile : ""}
        fontFamily="numberFontFamily"
      />
      <FieldShow
        title="Email"
        value={record ? record?.sEmail : ""}
        link={`mailto:${record ? record?.sEmail : ""}`}
      />
      <FieldShow
        title="SSN"
        value={recordData?.idtype == "ssn" ? recordData.sSSN : ""}
        fontFamily="numberFontFamily"
      />
      <FieldShow
        title="Driver's License"
        value={recordData?.idtype == "driver" ? recordData.driverId : ""}
        fontFamily="numberFontFamily"
      />
      <FieldShow
        title="Passport ID"
        value={
          recordData?.idtype == "non-usa-passport"
            ? recordData.nonus_passport
            : ""
        }
        fontFamily="numberFontFamily"
      />
      <FieldShow
        title="Green Card"
        value={record?.idtype == "greencard" ? record.greencard : ""}
        fontFamily="numberFontFamily"
      />
      <FieldShow
        title="Other ID"
        value={
          recordData?.idtype != "ssn" &&
          recordData?.idtype != "driver" &&
          recordData?.idtype != "greencard" &&
          recordData?.idtype != "non-usa-passport"
            ? recordData.sSSN
            : ""
        }
        fontFamily="numberFontFamily"
      />
      <FieldShow
        title="Date of Birth"
        value={recordData?.sBirth.replace(
          /(\d{2})\/(\d{2})\/(\d{4})/,
          "$3-$1-$2"
        )}
        fontFamily="numberFontFamily"
      />
      <FieldShow title="Address" value={recordData?.sStreet} />
      <FieldShow
        title=""
        value={
          recordData.sCity + ` ` + recordData.sState + ` ` + recordData.sZip
        }
      />
      <FieldShow
        title="Counselor"
        value={record ? record?.counselor_name : ""}
        fontFamily="numberFontFamily"
      />
      <FieldShow
        title="Employer"
        value={record ? record?.employment_company : ""}
        fontFamily="numberFontFamily"
      />
    </>
  );
};

const LoadCoApplicant = ({ ...props }) => {
  const paramAppId = props.appId;
  let appId = "";
  let record = props.record;
  if (paramAppId != undefined && record != undefined) {
    appId = paramAppId;
  } else {
    const controllerProps = useShowController();
    record = controllerProps.record;
    appId = record ? record.id : "";
  }

  if (!record || !record.id || !record.coapplicant_id) {
    return <>N/A</>;
  }

  const [recordData, setRecordData] = useState<any>(null);
  const { successNotify, errorNotify, infoNotify } = useNotify();
  const { data, isPending, error } = useGetOne("applications/getCoApplicant", {
    id: record.id,
  });
  useEffect(() => {
    setRecordData(data);
  }, [record.id, data]);

  if (error) {
    errorNotify("Error loading Application " + error);
  }
  if (isPending) {
    return <Typography>Co Application Loading</Typography>;
  }
  if (!recordData) {
    return "No Data";
  }

  return (
    <>
      <FieldShow
        title="Name"
        value={
          (recordData && recordData?.sFname ? recordData?.sFname : "") +
          ` ` +
          (recordData && recordData?.sLname ? recordData?.sLname : "")
        }
      />
      <FieldShow
        title="Relation"
        value={recordData ? recordData?.sRelationship : ""}
      />
      <FieldShow
        title="Mobile"
        value={recordData ? recordData?.sMobile : ""}
        fontFamily="numberFontFamily"
      />
      <FieldShow title="Email" value={recordData ? recordData?.sEmail : ""} />
      <FieldShow
        title="SSN"
        value={recordData ? recordData?.sSSN : ""}
        fontFamily="numberFontFamily"
      />
      <FieldShow
        title="Date of Birth"
        value={recordData ? recordData?.sBirth : ""}
        fontFamily="numberFontFamily"
      />
      {/* <FieldShow
        title="Address"
        value={recordData ? recordData?.address : ""}
      /> */}
      <FieldShow title="Address" value={recordData?.sStreet} />
      <FieldShow
        title=""
        value={
          recordData.sCity + ` ` + recordData.sState + ` ` + recordData.sZip
        }
      />
      <FieldShow
        title="Company"
        value={recordData ? recordData?.sCompany : ""}
      />
    </>
  );
};

const LoadGuarantor = ({ ...props }) => {
  const paramAppId = props.appId;
  let appId = "";
  let record = props.record;
  if (paramAppId != undefined && record != undefined) {
    appId = paramAppId;
  } else {
    const controllerProps = useShowController();
    record = controllerProps.record;
    appId = record ? record.id : "";
  }

  if (
    !record ||
    record == undefined ||
    !record.id ||
    !record.guarantor_id ||
    record.guarantor_id == null ||
    record.guarantor_id == 0 ||
    record.guarantor_id == "0" ||
    record.guarantor_id == undefined
  ) {
    return <>N/A</>;
  }

  const [recordData, setRecordData] = useState<any>(null);
  const { successNotify, errorNotify, infoNotify } = useNotify();
  const { data, isPending, error } = useGetOne("applications/getGuarantor", {
    id: record.id,
  });
  useEffect(() => {
    setRecordData(data);
  }, [record.id, data]);

  if (error) {
    errorNotify("Error loading Application " + error);
  }
  if (isPending) {
    return <Typography>Guanantor Loading</Typography>;
  }
  if (!recordData) {
    return "No Data";
  }

  return (
    <>
      <FieldShow
        title="Name"
        value={
          (recordData && recordData?.sFname ? recordData?.sFname : "") +
          ` ` +
          (recordData && recordData?.sLname ? recordData?.sLname : "")
        }
        fontFamily="numberFontFamily"
      />
      <FieldShow title="Mobile" value={recordData ? recordData?.sMobile : ""} />
      <FieldShow title="Email" value={recordData ? recordData?.sEmail : ""} />
      <FieldShow title="SSN" value={recordData ? recordData?.sSSN : ""} />
      <FieldShow
        title="Date of Birth"
        value={recordData ? recordData?.sBirth : ""}
      />
      {/* <FieldShow
        title="Address"
        value={recordData ? recordData?.address : ""}
      /> */}
      <FieldShow title="Address" value={recordData?.address_line1} />
      <FieldShow
        title=""
        value={recordData.city + ` ` + recordData.state + ` ` + recordData.zip}
      />
      <FieldShow
        title="Company"
        value={recordData ? recordData?.sCompany : ""}
      />
    </>
  );
};

const PreferredFrequency = ({ paymentFreq }: { paymentFreq: string }) => {
  if (paymentFreq == "M11" || paymentFreq == "M13") {
    return "Monthly";
  } else if (paymentFreq == "M12") {
    return "Semimonthly";
  } else if (paymentFreq == "W11") {
    return "Weekly";
  } else if (paymentFreq == "W21") {
    return "Biweekly";
  } else {
    return "N/A";
  }
};

const ApplicationBaseDataAside = ({ ...props }) => {
  const isMounted = useRef(false);
  const renderCountRef = useRef(0);
  const { successNotify, errorNotify, infoNotify } = useNotify();
  const { openGlobalTabs } = useContext(OpenTabsContext);
  const navigate = useNavigate();
  const paramAppId = props.appId;
  let appId = paramAppId;
  let record = props.record;
  let appStatus = props?.appStatus;

  useEffect(() => {}, [appStatus]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      renderCountRef.current += 1;
    }
  });
  const LoanNumberLinkField = () => {
    const handleClick = (id: any, loannum: any) => {
      if (openGlobalTabs.length >= 6) {
        if (openGlobalTabs.includes("loan-" + appId + "-" + loannum)) {
          navigate(`/loanSummary/` + appId + `?loannum=` + loannum);
        } else {
          errorNotify("Cannot open more than 6 tabs.");
          return false;
        }
      } else {
        navigate(`/loanSummary/` + appId + `?loannum=` + loannum);
      }
    };

    return (
      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
        <b>Loan ID : </b>
        {record.sLoanNum ? (
          <Link
            component="span"
            onClick={() => handleClick(paramAppId, record.sLoanNum)}
            className="numericText"
            underline="none"
            sx={{
              cursor: "pointer",
              textDecoration: "none",
            }}
          >
            {record.sLoanNum}
          </Link>
        ) : (
          "N/A"
        )}
      </Typography>
    );
  };

  let randBgColor = "white";
  const handlePrintHistory = () => {
    const style = document.createElement("style");
    style.innerHTML = `
    @media print {
      body * {
        visibility: hidden;
      }
      [class*="printBody-${appId}"],
      [class*="printBody-${appId}"] * {
        visibility: visible;
         overflow: visible !important;
        max-height: none !important;
        height: auto !important;
      }
      [class*="printBody-${appId}"]{
        position: absolute;
        left: 0;
        top: 0;        
        width:100% !important;
      }
    }
  `;
    document.head.appendChild(style);

    window.print();
    setTimeout(() => {
      document.head.removeChild(style);
    }, 100);
  };
  let showPrintButton = false;
  if (
    appStatus == "Approved" ||
    appStatus == "Completed" ||
    appStatus == "Funded"
  ) {
    if (record?.sExecuteStatus == "Closing Dos Received") {
      showPrintButton = true;
    }
  }
  if (appStatus == "Declined") {
    showPrintButton = true;
  }
  return (
    <>
      <CustomAsideCard
        sx={{ p: 1, mt: 1, backgroundColor: "#" + randBgColor }}
        // id="aside-application-summary"
        id={"aside-application-summary-" + appId}
        className={"printBody-" + appId}
      >
        <HmSubHeaderTitle title={`Application ` + paramAppId} />
        {showPrintButton ? (
          <ApplicationPrint appId={paramAppId} record={record} />
        ) : null}
        <AppStageStatus
          appStatus={appStatus}
          record={record}
          appId={appId}
          requirementSaved={props?.requirementSaved}
        />
        {appStatus == "Funded" ? (
          <LoanNumberLinkField />
        ) : (
          <FieldShow
            title="Loan ID"
            value={record?.sLoanNum || "N/A"}
            disabled="Yes"
          />
        )}
        <FieldShow title="Product Applied" value={record.sProductName} />
        <FieldShow
          title="Amount Applied"
          value={formatCurrency(record.amountSubmit)}
          fontFamily="numberFontFamily"
        />
        <FieldShow
          title="Term Applied"
          value={formatInt(record.termSubmit)}
          fontFamily="numberFontFamily"
          endText="months"
        />
        <FieldShow
          title="Collateral"
          value={record?.collateral?.haveCarDetail}
        />
        {record?.collateral?.haveCarDetail == "Yes" && (
          <CollateralDetailDialog data={record?.collateral} />
        )}

        {/* <FieldShow
          title="Collateral Value"
          value={formatCurrency(record.collateralValue)}
        /> */}
        <FieldShow title="Stated Purpose" value={record.sPurpose} />
        <FieldShow
          title="Preferred Payment Frequency"
          value={
            <PreferredFrequency paymentFreq={record?.attrs?.paymentFreq} />
          }
        />
        <FieldShow
          title="Preferred Payment Due Date"
          value={record?.attrs?.paymentDayFirst || "N/A"}
          fontFamily="numberFontFamily"
        />
        {/* <FieldShow title="Application Status" value={appStatus} /> */}
        {/* <ReferenceField /> */}
        {record?.counselor_name && (
          <>
            <FieldShow
              title="Referral Agency"
              value={record?.counselor_agency || "N/A"}
              fontFamily="numberFontFamily"
            />
            <FieldShow
              title="Referral Name"
              value={record?.counselor_name || "N/A"}
              fontFamily="numberFontFamily"
            />
          </>
        )}
        {/* It's Reference info, referral mean counselor or employer */}
        {record?.referralList &&
          record?.referralList.length > 0 &&
          record?.referralList.map((referral: any, index: number) => (
            <React.Fragment
              key={`${referral?.sName || "reference"}-${
                referral?.sEmail || "contact"
              }-${index}`}
            >
              <FieldShow
                title="Reference Name"
                value={referral?.sName || ""}
                fontFamily="numberFontFamily"
              />
              <FieldShow
                title="Reference Contact"
                value={referral?.sEmail || ""}
                fontFamily="numberFontFamily"
              />
            </React.Fragment>
          ))}

        <BaseCustomTab appId={paramAppId} record={record} />
      </CustomAsideCard>
    </>
  );
};

export default ApplicationBaseDataAside;
